import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';

import { SessionStatus, SessionStatusLabel } from '@core/models/brijjsports.enums';
import { ParticipantScore } from '@core/models/brijjsports.models';
import { archerSessionTotal, tenCount, totalArrows, xCount } from '@core/utils/scoring.util';
import { BrijjSportsStateService } from '@core/state/brijjsports-state.service';

import { PageHeaderComponent } from '@shared/components/page-header/page-header.component';
import { StatTileComponent } from '@shared/components/stat-tile/stat-tile.component';

interface ArcheryLeaderboardRow {
  athleteId: string;
  rank: number;
  name: string;
  initials: string;
  total: number;
  xs: number;
  tens: number;
  arrows: number;
  pbr: boolean;
}

@Component({
  selector: 'app-archery-spectator',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PageHeaderComponent, StatTileComponent],
  templateUrl: './archery-spectator.component.html',
  styleUrl: './archery-spectator.component.scss'
})
export class ArcherySpectatorComponent {
  readonly sessionId = input.required<string>();

  protected readonly state = inject(BrijjSportsStateService);

  protected readonly session = computed(() => this.state.sessionById(this.sessionId()));

  protected readonly statusText = computed(() => {
    const s = this.session();
    return s ? SessionStatusLabel[s.status ?? SessionStatus.Idle] : '';
  });

  protected readonly statusTone = computed<'green' | 'gold' | 'blue' | 'neutral'>(() => {
    switch (this.session()?.status) {
      case SessionStatus.Active:
        return 'green';
      case SessionStatus.Paused:
        return 'gold';
      case SessionStatus.Completed:
        return 'blue';
      default:
        return 'neutral';
    }
  });

  protected readonly isLive = computed(() => this.session()?.status === SessionStatus.Active);

  protected readonly subtitleParts = computed(() => {
    const s = this.session();
    if (!s) return '';
    const parts: string[] = [];
    if (s.indoor) parts.push('Indoor');
    if (s.compoundInner10) parts.push('Compound · inner-10');
    if (s.rounds?.length) {
      const arrows = s.rounds.reduce((sum, r) => sum + r.endsCount * r.arrowsPerEnd, 0);
      parts.push(`${s.rounds.length} round${s.rounds.length === 1 ? '' : 's'} · ${arrows} arrows`);
    }
    return parts.join(' · ');
  });

  protected readonly rows = computed<ArcheryLeaderboardRow[]>(() => {
    const session = this.session();
    if (!session) return [];
    const compound = session.compoundInner10 ?? false;
    const scores: ParticipantScore[] = session.scores ?? [];
    return scores
      .map(score => {
        const athlete = this.state.athleteById(score.athleteId);
        const name = athlete?.name ?? 'Unknown';
        return {
          athleteId: score.athleteId,
          rank: 0,
          name,
          initials: athlete?.initials ?? deriveInitials(name),
          total: score.total || archerSessionTotal(score, compound),
          xs: xCount(score),
          tens: tenCount(score),
          arrows: score.rounds.reduce((sum, r) => sum + r.ends.reduce((a, end) => a + end.length, 0), 0),
          pbr: score.pbr
        };
      })
      .sort((a, b) => b.total - a.total)
      .map((r, i) => ({ ...r, rank: i + 1 }));
  });

  protected readonly leadingRow = computed(() => this.rows()[0] ?? null);

  protected readonly arrowsRecorded = computed(() => this.rows().reduce((sum, r) => sum + r.arrows, 0));
  protected readonly arrowsCapacity = computed(() => {
    const session = this.session();
    if (!session?.rounds?.length) return 0;
    return totalArrows(session.rounds) * (session.participantIds?.length ?? this.rows().length);
  });

  protected readonly archerCount = computed(() => this.rows().length);
  protected readonly topXs = computed(() => this.leadingRow()?.xs ?? 0);

  trackRow(_: number, r: ArcheryLeaderboardRow): string {
    return r.athleteId;
  }
}

function deriveInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (!parts.length) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '?';
}
